import { DateRangeFilter } from '@/app/constants/delegation/dateRangeFilter'
import { ReportType } from '@/app/constants/reportType'
import {
  faClockDeskSolid,
  faClose,
  faCopySolid,
  faFlagswallowtailRegular,
  FaIcon,
  faPencilSolid,
  faPlus,
  faPrintSolid,
  faRefreshSolid,
  faTrashSolid,
} from '@/lib/fa'
import { format } from 'date-fns'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { TRIPS_FILTER_OPTIONS } from '@/modules/shared/constants/trips'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import React from 'react'
import { createPortal } from 'react-dom'
import { generatePath } from 'react-router-dom'
import BusinessTripsPrintModal from '../BusinessTripsPrintModal'
import { driversRoutesMap } from '@/modules/drivers/routes-config'
import { getDateRangeFilterName } from '@/app/common/converters/newDelegation/dateRangeFilterToTextConverter'
import { toast } from '@/lib/toasts/api'
import usePrintService from '@/modules/drivers/services/PrintService'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'
import { useTask } from '@/modules/shared/context/TaskContext'
import { DatePickerRangeProps } from '@/modules/drivers/types'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { TaskType } from '@/app/constants/taskType'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import { BusinessTripModel } from '@/app/models/businessTripModel'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import CopyTripsToDriverModal from '../CopyTripsToDriverModal'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'

type MatchParams = {
  companyId: string
  driverId?: string
  from?: string
  to?: string
}

type Props = {
  dateRange: DatePickerRangeProps
  dateRangeFilter: DateRangeFilter
  polishOnlyFilter: boolean
  onDateRangeFilterChange: (filter: DateRangeFilter) => void
  onPolishOnlyFilterChange: (onlyPolish: boolean) => void
  match?: RouteComponentProps<MatchParams>['match']
  history?: RouteComponentProps['history']
  companyId?: string
  driverId?: string
  onRefresh?: () => void
  from: string
  to: string
  setShowAuditDates: React.Dispatch<React.SetStateAction<boolean>>
  showAuditDates: boolean
  selectedTrips: BusinessTripModel[]
  resetAll: () => void
  selectedDriverId: string
  isSelectedAny: boolean
}

const BusinessTripSingleActionBar: React.FC<Props> = ({
  dateRangeFilter,
  polishOnlyFilter,
  onDateRangeFilterChange,
  onPolishOnlyFilterChange,
  match,
  history,
  companyId,
  isSelectedAny,
  driverId,
  onRefresh,
  from,
  to,
  dateRange,
  setShowAuditDates,
  showAuditDates,
  selectedTrips,
  selectedDriverId,
  resetAll,
}) => {
  const { t } = useLocalize()
  const [printModalOpen, setPrintModalOpen] = React.useState(false)
  const [copyModalOpen, setCopyModalOpen] = React.useState(false)
  const [separateFiles, setSeparateFiles] = React.useState(false)
  const { getPdf } = usePrintService()
  const { addTask } = useTask()
  const { formatApiDate } = useDate()
  const { isGenerating, busyTooltip, runPrintJob } = usePrintJobGuard()
  const { deleteBusinessTrips } = useBusinessTripService()
  const confirm = useConfirm()

  const handleAddClick = () => {
    const newPath = generatePath(driversRoutesMap.driverGroupBusinessTripNew, {
      companyId: companyId,
      driverId: driverId,
      from,
      to,
    })
    history.push(newPath)
  }
  const handlePrintClick = () => {
    if (printProps) {
      setPrintModalOpen(true)
    } else if (handlePrintGenerate) {
      handlePrintGenerate([ReportType.BASIC_TRIP_PRINT], {
        generateToSeparateFiles: separateFiles,
        rememberSelected: false,
      })
    }
  }

  const handlePrintGenerate = React.useCallback(
    (
      selectedReports: ReportType[],
      options: { generateToSeparateFiles: boolean; rememberSelected: boolean },
    ) =>
    runPrintJob(async () => {
      if (!dateRange?.startDate || !dateRange?.endDate || !formatApiDate) {
        toast.warning({
          text: t('menu.select-date-range'),
        })
        return
      }

      if (selectedReports.length === 0) {
        return
      }

      try {
        const startDate = new Date(
          dateRange.startDate.year!,
          dateRange.startDate.month! - 1,
          dateRange.startDate.day!,
        )
        const endDate = new Date(
          dateRange.endDate.year!,
          dateRange.endDate.month! - 1,
          dateRange.endDate.day!,
        )

        const startTime = formatApiDate(startDate)
        const endTime = encodeURIComponent(
          `${format(endDate, 'YYYY-MM-DD')} 23:59`,
        )

        for (const reportType of selectedReports) {
          try {
            const taskId = await getPdf({
              companyId,
              reportType,
              drivers: [driverId],
              startTime,
              endTime,
              data: {
                trips: null,
                generateToSeparateFiles: options.generateToSeparateFiles,
                rememberSelected: options.rememberSelected,
                onlyPolish: polishOnlyFilter,
                tripReportOption: dateRangeFilter,
              },
            })
            addTask(taskId, TaskType.Printout, reportType)
          } catch (error) {
            console.error(`Error generating print for ${reportType}:`, error)
          }
        }

        setPrintModalOpen(false)
      } catch (error) {
        console.error('Error generating print:', error)
        toast.error({
          text: t('error.print-failed'),
        })
      }
    }),
    [
      dateRange,
      formatApiDate,
      companyId,
      driverId,
      getPdf,
      addTask,
      runPrintJob,
      t,
      polishOnlyFilter,
      dateRangeFilter,
    ],
  )
  const handleEditClick = () => {
    if (!history) return
    // Bez driverId generatePath rzuca "Missing :driverId param" wprost w handlerze
    // zdarzenia - ErrorBoundary tego nie lapie, wiec przycisk po prostu nic nie robil.
    if (!selectedDriverId) return

    const selectedTripId = selectedTrips[0].id
    const url = generatePath(driversRoutesMap.driverBusinessTripEdit, {
      companyId,
      driverId: selectedDriverId,
      id: String(selectedTripId),
    })
    history.push(url)
  }
  const handleDelete = async () => {
    if (selectedTrips.length === 0) return

    const ok = await confirm({
      title: t('operation.confirm-delete') || t('operation.delete'),
      message:
        selectedTrips.length === 1
          ? t('businessTrip.delete-confirmation-single')
          : t('businessTrip.delete-confirmation-multiple').replace(
              '{0}',
              String(selectedTrips.length),
            ),
      confirmText: t('operation.delete') || 'Usuń',
      cancelText: t('operation.cancel') || 'Anuluj',
    })
    if (!ok) return

    try {
      const idsToDelete = selectedTrips.map((trip) => Number(trip.id))

      await deleteBusinessTrips({
        companyId,
        ids: idsToDelete,
      })

      toast.success({
        text: t('businessTrip.trips-deleted'),
        title: t('businessTrip.trips-deleted-title'),
      })
      onRefresh()
    } catch (error) {
      console.error('Error deleting business trips:', error)
      toast.error({
        text: t('error.delete-failed-message'),
        title: t('error.delete-failed'),
      })
    }
  }
  const printProps = React.useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate || !formatApiDate) {
      return undefined
    }

    const startDate = new Date(
      dateRange.startDate.year!,
      dateRange.startDate.month! - 1,
      dateRange.startDate.day!,
    )
    const endDate = new Date(
      dateRange.endDate.year!,
      dateRange.endDate.month! - 1,
      dateRange.endDate.day!,
    )

    return {
      companyId,
      driverIds: [driverId],
      startTime: startDate,
      endTime: new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
        23,
        59,
        0,
        0,
      ),
    }
  }, [dateRange, formatApiDate, companyId, driverId])

  return (
    <>
      <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
        {/* Left Side */}
        <div className="tw-flex tw-items-center tw-gap-4">
          {/* Add Delegation Button - Only in single view */}

          <>
            <Tooltip text={t('businessTrip.add')} onClick={handleAddClick}>
              <div className="tw-flex tw-h-5 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faPlus}
                  className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
                />
                <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
                  {t('businessTrip.add')}
                </span>
              </div>
            </Tooltip>
            <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]"></div>
            <Tooltip
              text={t('businessTrip.edit-delegation')}
              onClick={() => handleEditClick()}
              disabled={!isSelectedAny || selectedTrips.length !== 1}
            >
              <div className="tw-flex tw-h-5 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faPencilSolid}
                  className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
                />
                <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
                  {t('businessTrip.edit-delegation')}
                </span>
              </div>
            </Tooltip>
          </>

          {/* Polish Only Filter */}
          <Tooltip text={t('businessTrip.onlyPolish') || 'Tylko polskie'}>
            <div
              className={`tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded ${
                polishOnlyFilter ? 'tw-bg-primary' : 'tw-bg-primary/30'
              } tw-cursor-pointer`}
              onClick={() => onPolishOnlyFilterChange(!polishOnlyFilter)}
            >
              <FaIcon
                icon={faFlagswallowtailRegular}
                className="tw-h-4 tw-w-4 tw-text-white"
              />
            </div>
          </Tooltip>

          {/* Date Range Filter Toggle Buttons */}
          <div className="tw-flex tw-items-stretch tw-rounded-md tw-bg-[#6B77A0]/30">
            {TRIPS_FILTER_OPTIONS.filter(
              (o) =>
                o.value !== DateRangeFilter.Calculated &&
                o.value !== DateRangeFilter.SettlementMonth &&
                o.value !== DateRangeFilter.AllButSettlementMonth &&
                (showAuditDates || o.value !== DateRangeFilter.Modified),
            ).map((option, index, arr) => {
              const filterName = getDateRangeFilterName(option.value, t)
              const isActive = dateRangeFilter === option.value
              const isFirst = index === 0
              const isLast = index === arr.length - 1
              return (
                <Tooltip key={option.value} text={filterName}>
                  <div
                    onClick={() => onDateRangeFilterChange(option.value)}
                    className={`tw-grid tw-cursor-pointer tw-place-items-center tw-p-1 ${
                      isFirst ? 'tw-rounded-s-md' : ''
                    } ${isLast ? 'tw-rounded-e-md' : ''} ${
                      isActive ? 'tw-bg-primary' : 'tw-bg-none'
                    }`}
                  >
                    <img
                      src={option.icon}
                      alt={filterName}
                      className="tw-h-4 tw-w-4 tw-brightness-0 tw-invert"
                    />
                  </div>
                </Tooltip>
              )
            })}
          </div>
          <Tooltip text={'Pokaż daty modyfikacji i utworzenia'}>
            <div
              className={`tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded ${
                showAuditDates ? 'tw-bg-primary' : 'tw-bg-primary/30'
              } tw-cursor-pointer`}
              onClick={() => setShowAuditDates(!showAuditDates)}
            >
              <FaIcon
                icon={faClockDeskSolid}
                className="tw-h-4 tw-w-4 tw-text-white"
              />
            </div>
          </Tooltip>
          <Tooltip
            text={t('businessTrip.copy-to-driver')}
            blockedText={
              selectedTrips.length === 0
                ? t('businessTrip.copy-select-trips-first')
                : undefined
            }
            onClick={() => setCopyModalOpen(true)}
          >
            <div className="tw-flex tw-h-5 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
              <FaIcon
                icon={faCopySolid}
                className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
              />
            </div>
          </Tooltip>
        </div>

        <div className="tw-flex tw-items-center tw-gap-4">
          <Tooltip
            text={t('businessTrip.remove')}
            disabled={!isSelectedAny || selectedTrips.length === 0}
            onClick={() => handleDelete()}
          >
            <div className="tw-flex tw-h-5 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
              <FaIcon
                icon={faTrashSolid}
                className="tw-h-4 tw-w-4 tw-text-red-500"
              />
            </div>
          </Tooltip>
          {printProps && (
            <>
              <Tooltip
                text={t('menu.print') || 'Drukuj'}
                onClick={handlePrintClick}
                blockedText={busyTooltip}
              >
                <FaIcon
                  icon={faPrintSolid}
                  className="tw-h-4 tw-w-4 tw-text-primary hover:tw-opacity-70"
                />
              </Tooltip>
            </>
          )}

          {onRefresh && (
            <Tooltip text={t('menu.refresh')} onClick={() => onRefresh?.()}>
              <FaIcon
                icon={faRefreshSolid}
                className="tw-h-4 tw-w-4 tw-text-primary hover:tw-opacity-70"
              />
            </Tooltip>
          )}
        </div>
      </div>

      {printProps &&
        createPortal(
          <BusinessTripsPrintModal
            isOpen={printModalOpen}
            onClose={() => setPrintModalOpen(false)}
            companyId={printProps.companyId}
            driverIds={printProps.driverIds}
            startTime={printProps.startTime}
            endTime={printProps.endTime}
            onGenerate={handlePrintGenerate}
            pending={isGenerating}
          />,
          document.body,
        )}
      {copyModalOpen && companyId && (
        <CopyTripsToDriverModal
          open={copyModalOpen}
          companyId={companyId}
          tripIds={selectedTrips.map((trip) => Number(trip.id))}
          onClose={() => setCopyModalOpen(false)}
          onCopied={() => {
            resetAll()
            onRefresh?.()
          }}
        />
      )}
      {/* {isSelectedAny && (
        <Tooltip text={t('common.close')} onClick={() => resetAll()}>
          <div className="tw-relative tw-cursor-pointer">
            <div className="tw-absolute -tw-top-1.5 tw-left-1/2 tw-z-0 tw-h-7 tw-w-7 tw--translate-x-1/2 tw-rounded-full tw-bg-white tw-p-2 tw-shadow lg:tw-top-1/2 lg:tw--translate-y-1/2"></div>
            <FaIcon icon={faClose} className="tw-relative" />
          </div>
        </Tooltip>
      )} */}
    </>
  )
}

export default BusinessTripSingleActionBar
